import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Switch,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  LinearProgress,
  Paper,
  Toolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  ArrowBack,
  Add,
  Language,
  Description,
  CloudUpload,
  Refresh,
  Psychology,
  ExpandMore,
} from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import CustomTabs from "../../common/custom-tabs";
import { ReactComponent as Edit } from "../../assets/images/icons/agentic_ai/edit.svg";
import { ReactComponent as Delete } from "../../assets/images/icons/agentic_ai/delete-red.svg";
import { ReactComponent as Upload } from "../../assets/images/icons/campaign/broadcast/upload-main.svg";

const SectionCard = ({
  icon,
  title,
  subtitle,
  toggle,
  checked,
  onToggle,
  children,
}) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid #DFE5EC",
      borderRadius: "12px",
      p: 3,
      height: "100%",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 2.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "8px",
            bgcolor: "#F0F7FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: ColorsConstants.primaryBackgroundColor,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography
            sx={{
              ...typography.h2,
              color: "#1D2330",
              lineHeight: 1.3,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{ ...typography.bodySmall, color: "#6C7C93" }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>
      {toggle && (
        <Switch
          checked={checked}
          onChange={onToggle}
          sx={{
            width: 44,
            height: 24,
            padding: 0,
            "& .MuiSwitch-switchBase": {
              padding: 0,
              margin: "3px",
              "&.Mui-checked": {
                transform: "translateX(20px)",
                color: "#fff",
                "& + .MuiSwitch-track": {
                  backgroundColor: ColorsConstants.primaryBackgroundColor,
                  opacity: 1,
                },
              },
            },
            "& .MuiSwitch-thumb": {
              width: 18,
              height: 18,
              boxShadow: "none",
            },
            "& .MuiSwitch-track": {
              borderRadius: 13,
              backgroundColor: "#C8D0E0",
              opacity: 1,
            },
          }}
        />
      )}
    </Box>
    {children}
  </Paper>
);

// ── Prompt Configuration Tab ──────────────────────────────────────────────────
function KnowledgeBaseTab() {
  const [prompt, setPrompt] = useState(
    "You are a helpful customer support assistant. Be friendly, professional, and concise in your responses. Always prioritize customer satisfaction while following company policies.",
  );
  const [collectData, setCollectData] = useState(true);
  const [variables, setVariables] = useState([
    {
      id: 1,
      name: "user_email",
      description: "Collect user email for follow-up",
    },
    { id: 2, name: "issue_type", description: "Category of the support issue" },
  ]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newVarName, setNewVarName] = useState("");
  const [newVarDesc, setNewVarDesc] = useState("");

  const handleAdd = () => {
    if (newVarName.trim()) {
      setVariables((v) => [
        ...v,
        {
          id: Date.now(),
          name: newVarName.trim(),
          description: newVarDesc.trim(),
        },
      ]);
    }
    setNewVarName("");
    setNewVarDesc("");
    setShowAddForm(false);
  };

  const handleCancel = () => {
    setNewVarName("");
    setNewVarDesc("");
    setShowAddForm(false);
  };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2.5 }}>
      {/* Prompt Card */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #DFE5EC",
          borderRadius: "12px",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography
          sx={{ ...typography.h2, color: "#1D2330" }}
        >
          Prompt
        </Typography>
        <TextField
          multiline
          rows={8}
          fullWidth
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              ...typography.bodyLarge,
              alignItems: "flex-start",
              backgroundColor: "#F3F4F6",
              "& fieldset": {
                borderColor: "#DCDFE5",
              },
              "&:hover fieldset": {
                borderColor: "#DCDFE5",
              },
              "&.Mui-focused fieldset": {
                borderColor: ColorsConstants.primaryBackgroundColor,
              },
            },
          }}
        />
        {/* Hint box */}
        <Box
          sx={{
            bgcolor: "#D3ECEE80",
            borderRadius: "8px",
            p: 2,
            border: "1px solid #D3ECEE ",
          }}
        >
          <Typography
            sx={{
              ...typography.bodySmall,
              color: "#6C7C93",
              lineHeight: 1.6,
            }}
          >
            This prompt controls how your AI responds, formats answers, and
            follows rules. Define the{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#266673" }}>
              tone
            </Box>
            ,{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#266673" }}>
              personality
            </Box>
            ,{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#266673" }}>
              rules
            </Box>
            ,{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#266673" }}>
              constraints
            </Box>
            , and{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#266673" }}>
              special behaviors
            </Box>{" "}
            for your bot.
          </Typography>
        </Box>
      </Paper>

      {/* Data Collection Card */}
      <Paper
        elevation={0}
        sx={{ border: "1px solid #DFE5EC", borderRadius: "12px", p: 3 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2.5,
          }}
        >
          <Typography
            sx={{ ...typography.h2, color: "#1D2330" }}
          >
            Data Collection
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              sx={{ ...typography.bodySmall, color: "#6C7C93" }}
            >
              Collect data after conversation
            </Typography>
            <Switch
              checked={collectData}
              onChange={() => setCollectData((v) => !v)}
              sx={{
                width: 44,
                height: 24,
                padding: 0,
                "& .MuiSwitch-switchBase": {
                  padding: 0,
                  margin: "3px",
                  "&.Mui-checked": {
                    transform: "translateX(20px)",
                    color: "#fff",
                    "& + .MuiSwitch-track": {
                      backgroundColor: ColorsConstants.primaryBackgroundColor,
                      opacity: 1,
                    },
                  },
                },
                "& .MuiSwitch-thumb": {
                  width: 18,
                  height: 18,
                  boxShadow: "none",
                },
                "& .MuiSwitch-track": {
                  borderRadius: 13,
                  backgroundColor: "#C8D0E0",
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>

        {/* Bordered Table */}
        <Box
          sx={{
            border: "1px solid #DFE5EC",
            borderRadius: "8px",
            overflow: "hidden",
            mb: 2,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#E8EAED80" }}>
                {["S.No", "Variable Name", "Description", "Action"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      ...typography.bodySmall,
                      fontWeight: 600,
                      color: "#6C7C93",
                      borderBottom: "1px solid #DFE5EC",
                      py: 1.25,
                      px: 2,
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {variables.map((row, i) => (
                <TableRow
                  key={row.id}
                  sx={{
                    "&:not(:last-child) td": {
                      borderBottom: "1px solid #DFE5EC",
                    },
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover": { bgcolor: "#FAFBFC" },
                  }}
                >
                  <TableCell
                    sx={{
                      ...typography.bodySmall,
                      color: "#6C7C93",
                      py: 1.75,
                      px: 2,
                      width: 52,
                    }}
                  >
                    {i + 1}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...typography.bodyLarge,
                      fontWeight: 500,
                      color: "#1D2330",
                      py: 1.75,
                      px: 2,
                    }}
                  >
                    {row.name}
                  </TableCell>
                  <TableCell
                    sx={{ ...typography.bodyLarge, color: "#6C7C93", py: 1.75, px: 2 }}
                  >
                    {row.description}
                  </TableCell>
                  <TableCell sx={{ py: 1.75, px: 2, width: 80 }}>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        sx={{
                          color: "#5B8DEF",
                          borderRadius: "6px",
                          p: 0.5,
                          "&:hover": { color: "#2450A8", bgcolor: "#EEF4FF" },
                        }}
                      >
                        <Edit sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        sx={{
                          color: "#E05252",
                          borderRadius: "6px",
                          p: 0.5,
                          "&:hover": { color: "#C62828", bgcolor: "#FFF0F0" },
                        }}
                        onClick={() =>
                          setVariables((v) => v.filter((x) => x.id !== row.id))
                        }
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>

        {/* Inline Add Variable Form */}
        {showAddForm ? (
          <Box
            sx={{
              bgcolor: "#E8EAED80",
              border: "1px solid #DCDFE5",
              borderRadius: "8px",
              p: 2,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    fontWeight: 500,
                    color: "#6C7C93",
                    mb: 0.75,
                  }}
                >
                  Variable Name
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g., user_email"
                  value={newVarName}
                  onChange={(e) => setNewVarName(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      ...typography.bodySmall,
                      bgcolor: "#FFFFFF",
                      "& fieldset": { borderColor: "#DFE5EC" },
                      "&:hover fieldset": { borderColor: "#B0BEC5" },
                      "&.Mui-focused fieldset": {
                        borderColor: ColorsConstants.primaryBackgroundColor,
                      },
                    },
                  }}
                  InputProps={{ sx: typography.bodySmall }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    fontWeight: 500,
                    color: "#6C7C93",
                    mb: 0.75,
                  }}
                >
                  Description
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g., Collect user email for follow-up"
                  value={newVarDesc}
                  onChange={(e) => setNewVarDesc(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                      ...typography.bodySmall,
                      bgcolor: "#FFFFFF",
                      "& fieldset": { borderColor: "#DFE5EC" },
                      "&:hover fieldset": { borderColor: "#B0BEC5" },
                      "&.Mui-focused fieldset": {
                        borderColor: ColorsConstants.primaryBackgroundColor,
                      },
                    },
                  }}
                  InputProps={{ sx: typography.bodySmall }}
                />
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <ButtonOne
                variant="contained"
                title="Add"
                sx={{ 
                  backgroundColor: ColorsConstants.primaryBlue100,
                  ...typography.bodyLarge 
                }}
                onClick={handleAdd}
              >
                Add
              </ButtonOne>
              <ButtonTwo
                variant="outlined"
                title="Cancel"
                onClick={handleCancel}
                sx={typography.bodyLarge}
              >
                Cancel
              </ButtonTwo>
            </Box>
          </Box>
        ) : (
          <Button
            startIcon={<Add />}
            size="small"
            sx={{
              color: ColorsConstants.primaryBackgroundColor,
              fontWeight: 500,
              pl: 0.5,
              ...typography.bodySmall,
            }}
            onClick={() => setShowAddForm(true)}
          >
            Add Variable
          </Button>
        )}
      </Paper>
    </Box>
  );
}

// ── Knowledge Base Tab ────────────────────────────────────────────────────────
function PromptConfigTab() {
  const [websiteEnabled, setWebsiteEnabled] = useState(true);
  const [documentEnabled, setDocumentEnabled] = useState(true);
  const [urlValue, setUrlValue] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const knowledgeSizeMB = 2.4;
  const maxSizeMB = 10;
  const progress = (knowledgeSizeMB / maxSizeMB) * 100;

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2.5,
          mb: 2.5,
        }}
      >
        <SectionCard
          icon={<Language sx={{ fontSize: 24, color: "#1B76D1" }} />}
          title="Website Knowledge Base"
          subtitle="Index content from websites"
          toggle
          checked={websiteEnabled}
          onToggle={() => setWebsiteEnabled((v) => !v)}
        >
          <Typography
            sx={{ ...typography.bodyLarge, fontWeight: 500, color: "#1D2330", mb: 1 }}
          >
            Website URLs
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter the website URL"
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  ...typography.bodyLarge,
                  backgroundColor: "#F3F4F6",
                  "& fieldset": {
                    borderColor: "#DCDFE5",
                  },
                  "&:hover fieldset": {
                    borderColor: "#DCDFE5",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: ColorsConstants.primaryBackgroundColor,
                  },
                },
              }}
              InputProps={{ sx: typography.bodyLarge }}
            />
            <IconButton
              size="small"
              sx={{
                bgcolor: "#E5E7EB",
                borderRadius: "8px",
                width: 36,
                height: 36,
                "&:hover": {
                  bgcolor: "#D1D5DB",
                },
              }}
            >
              <Add sx={{ fontSize: 20, color: "#4B5563" }} />
            </IconButton>
          </Box>
          <Typography sx={{ ...typography.bodySmall, mt: 1.5, color: "#6C7C93" }}>
            Content from these websites will be indexed and used for AI
            responses.
          </Typography>
        </SectionCard>
        <SectionCard
          icon={<Description sx={{ fontSize: 24 }} />}
          title="Document Knowledge Base"
          subtitle="Upload PDF documents"
          toggle
          checked={documentEnabled}
          onToggle={() => setDocumentEnabled((v) => !v)}
        >
          <Typography
            sx={{ ...typography.bodyLarge, fontWeight: 500, color: "#1D2330", mb: 1 }}
          >
            Upload Documents
          </Typography>
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
            }}
            sx={{
              border: `2px dashed ${dragOver ? ColorsConstants.primaryBackgroundColor : "#DCDFE5"}`,
              borderRadius: "8px",
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.75,
              bgcolor: dragOver ? "#F0F7FF" : "#FFFFFF",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: ColorsConstants.primaryBackgroundColor,
                bgcolor: "#F0F7FF",
              },
            }}
            onClick={() => document.getElementById("file-input")?.click()}
          >
            <input id="file-input" type="file" accept=".pdf" hidden />
            <Upload sx={{ fontSize: 32 }} />
            <Typography
              sx={{ ...typography.bodyLarge, color: "#6C7C93" }}
            >
              <Box
                component="span"
                sx={{
                  color: ColorsConstants.primaryBackgroundColor,
                  fontWeight: 500,
                }}
              >
                Click to upload
              </Box>{" "}
              or drag and drop
            </Typography>
            <Typography sx={{ ...typography.bodySmall, color: "#9AA6B8" }}>
              PDF only (max 10MB)
            </Typography>
          </Box>
          <Typography sx={{ ...typography.bodySmall, mt: 1.5, color: "#6C7C93" }}>
            Upload internal documents to train the AI with private knowledge.
          </Typography>
        </SectionCard>
      </Box>

      <Paper
        elevation={0}
        sx={{ border: "1px solid #DFE5EC", borderRadius: "12px", p: 3 }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Typography
            sx={{ ...typography.h2, color: "#1D2330" }}
          >
            Training Information
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <ButtonTwo
              variant="outlined"
              startIcon={<Refresh />}
              title="Refresh Status"
              sx={typography.bodyLarge}
            >
              Refresh Status
            </ButtonTwo>
            <ButtonOne
              variant="contained"
              startIcon={<Psychology />}
              sx={{ 
                backgroundColor: ColorsConstants.primaryBlue100,
                ...typography.bodyLarge 
              }}
              title="Train Model"
            >
              Train Model
            </ButtonOne>
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 3,
          }}
        >
          {[
            {
              label: "Knowledge Base Size",
              value: (
                <Box>
                  <Typography
                    sx={{
                      ...typography.h1,
                      color: "#1D2330",
                      mb: 0.75,
                    }}
                  >
                    {knowledgeSizeMB} MB / {maxSizeMB} MB
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      width: 120,
                      borderRadius: "4px",
                      height: 6,
                      backgroundColor: "#DFE5EC",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: ColorsConstants.primaryPurple100,
                      },
                    }}
                  />
                </Box>
              ),
            },
            { label: "Training Status", value: "Ready to train" },
            { label: "Last Training", value: "Never" },
            { label: "Training Source", value: "—" },
          ].map((item, i) => (
            <Box key={i}>
              <Typography
                sx={{
                  ...typography.bodySmall,
                  color: "#6C7C93",
                  mb: 0.75,
                }}
              >
                {item.label}
              </Typography>
              {typeof item.value === "string" ? (
                <Typography
                  sx={{ ...typography.bodyLarge, fontWeight: 500, color: "#1D2330" }}
                >
                  {item.value}
                </Typography>
              ) : (
                item.value
              )}
            </Box>
          ))}
        </Box>
      </Paper>
    </>
  );
}

// ── Root Page ─────────────────────────────────────────────────────────────────
export default function KnowledgeBasePage(props) {
  const [activeTab, setActiveTab] = useState("knowledge");
  const [config, setConfig] = useState("default");

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue === 0 ? "knowledge" : "prompt");
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#FAFAFA" }}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: "1px solid #DFE5EC",
          borderRadius: 0,
          bgcolor: "#FFFFFF",
        }}
      >
        <Toolbar sx={{ px: 3, gap: 2, minHeight: "64px !important" }}>
          <IconButton
            size="small"
            sx={{ color: "#6C7C93" }}
            onClick={() => props.onNavigate("skills")}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
          <Typography
            sx={{
              ...typography.h1,
              color: "#1D2330",
              mr: "auto",
            }}
          >
            Customer Support Bot
          </Typography>
          <CustomTabs
            value={activeTab === "knowledge" ? 0 : 1}
            onChange={handleTabChange}
            tabs={[
              { label: "Knowledge Base", value: 0 },
              { label: "Prompt Configuration", value: 1 },
            ]}
          />
          <ButtonTwo
            variant="outlined"
            title="Try it"
            sx={{
              backgroundColor: "#F3F4F6",
              color: "#2450A8",
              borderColor: "#2450A8",
              ...typography.bodyLarge,
              "&:hover": {
                backgroundColor: "#EEEEEE",
                borderColor: ColorsConstants.primaryBackgroundColor,
              },
            }}
          >
            Try it
          </ButtonTwo>
          <ButtonOne
            variant="contained"
            sx={{ 
              backgroundColor: ColorsConstants.primaryBlue100,
              ...typography.bodyLarge 
            }}
            title="Train Agent"
          >
            Train Agent
          </ButtonOne>
        </Toolbar>
      </Paper>

      <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1.5,
            mb: 3,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={config}
              onChange={(e) => setConfig(e.target.value)}
              sx={{
                height: "40px",
                borderRadius: "8px",
                ...typography.bodyLarge,
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#DFE5EC",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#DFE5EC",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: ColorsConstants.primaryBackgroundColor,
                },
                "& .MuiSelect-select": {
                  padding: "8.5px 14px",
                  ...typography.bodyLarge,
                },
              }}
              IconComponent={ExpandMore}
            >
              <MenuItem value="default" sx={{ ...typography.bodyLarge }}>
                Default Configuration
              </MenuItem>
              <MenuItem value="custom1" sx={{ ...typography.bodyLarge }}>
                Custom Config 1
              </MenuItem>
            </Select>
          </FormControl>
          <ButtonOne
            variant="contained"
            startIcon={<Add />}
            title="Create Configuration"
            sx={{
              backgroundColor: ColorsConstants.primaryPurple100,
              color: "#FFFFFF",
              ...typography.bodyLarge,
              "&:hover": {
                backgroundColor: ColorsConstants.primaryPurple100,
                opacity: 0.9,
              },
              "& .MuiButton-startIcon": { color: "#FFFFFF" },
            }}
          >
            Create Configuration
          </ButtonOne>
          <ButtonOne
            variant="contained"
            sx={{ 
              backgroundColor: ColorsConstants.primaryBlue100, 
              px: 3,
              ...typography.bodyLarge 
            }}
            title="Update"
          >
            Update
          </ButtonOne>
        </Box>

        {activeTab === "knowledge" ? <KnowledgeBaseTab /> : <PromptConfigTab />}
      </Box>
    </Box>
  );
}